import React, { useMemo } from 'react';
import dayjs from 'dayjs';

import { useMobile } from 'src/theme/responsive';

interface DayColumnWrapperProps {
    children: React.ReactNode;
    className?: string;
    date: Date;
    style?: React.CSSProperties;
}

interface Options {
    min: Date;
    max: Date;
    onSelectSlot: (start: Date) => void;
    stepMinutes?: number;
    testId: string;
}

export function useMobileCalendarDaySlot({
    min,
    max,
    onSelectSlot,
    stepMinutes = 30,
    testId,
}: Options) {
    const isMobile = useMobile();

    const dayColumnWrapper = useMemo(
        () => React.forwardRef<HTMLDivElement, DayColumnWrapperProps>(
            function MobileCalendarDayColumn(
                { children, className, date, style },
                ref
            ) {
                return (
                    <div
                        ref={ref}
                        className={className}
                        style={style}
                        data-testid={testId}
                        onClick={(event) => {
                            if ((event.target as HTMLElement).closest('.rbc-event')) {
                                return;
                            }

                            const bounds = event.currentTarget.getBoundingClientRect();
                            const minMinutes = min.getHours() * 60 + min.getMinutes();
                            const maxMinutes = max.getHours() * 60 + max.getMinutes();
                            const clickRatio = (event.clientY - bounds.top) / bounds.height;
                            const clickedMinutes =
                                minMinutes + clickRatio * (maxMinutes - minMinutes);
                            const slotMinutes =
                                Math.floor(clickedMinutes / stepMinutes) * stepMinutes;

                            onSelectSlot(
                                dayjs(date)
                                    .startOf('day')
                                    .add(slotMinutes, 'minute')
                                    .toDate()
                            );
                        }}
                    >
                        {children}
                    </div>
                );
            }
        ),
        [max, min, onSelectSlot, stepMinutes, testId]
    );

    return {
        isMobile,
        dayColumnWrapper: isMobile ?
            dayColumnWrapper as unknown as React.ComponentType :
            undefined,
    };
}
